import { libraryItemPendingChangesGuard } from './features/mvp-reset/pages/library-item-detail-page/library-item-pending-changes.guard';
import { inject } from '@angular/core';
import { ClerkCustomerWebService } from './core/auth/clerk-customer-web.service';
import { smartAddPendingChangesGuard } from './features/mvp-reset/pages/smart-add-page/smart-add-pending-changes.guard';
import { type Routes } from '@angular/router';
import { authRouteGuard } from './core/auth/auth-route.guard';
import { type AppShellRouteData } from './core/shell/types/app-shell-route-data.interface';

type WishlistDetailRouteMode = 'authenticated' | 'public';

type WishlistDetailAppRouteData = AppShellRouteData & {
  readonly wishlistDetailMode: WishlistDetailRouteMode;
};

export const appRoutes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'timeline',
  },
  {
    path: 'login',
    loadComponent: async (): Promise<typeof import('./features/auth/pages/auth-route-page.component').AuthRoutePageComponent> =>
      (await import('./features/auth/pages/auth-route-page.component')).AuthRoutePageComponent,
    data: {
      authAction: 'login',
      shellMode: 'bare',
    },
  },
  {
    path: 'signup',
    loadComponent: async (): Promise<typeof import('./features/auth/pages/auth-route-page.component').AuthRoutePageComponent> =>
      (await import('./features/auth/pages/auth-route-page.component')).AuthRoutePageComponent,
    data: { authAction: 'signup', shellMode: 'bare' },
  },
  {
    path: 'callback',
    loadComponent: async (): Promise<typeof import('./features/auth/pages/auth-route-page.component').AuthRoutePageComponent> =>
      (await import('./features/auth/pages/auth-route-page.component')).AuthRoutePageComponent,
    data: {
      authAction: 'callback',
      shellMode: 'bare',
    },
  },
  {
    path: 'logout',
    loadComponent: async (): Promise<typeof import('./features/auth/pages/auth-route-page.component').AuthRoutePageComponent> =>
      (await import('./features/auth/pages/auth-route-page.component')).AuthRoutePageComponent,
    data: {
      authAction: 'logout',
      shellMode: 'bare',
    },
  },
  {
    path: 'app',
    pathMatch: 'full',
    redirectTo: 'timeline',
  },
  {
    path: 'onboarding',
    canActivate: [authRouteGuard],
    loadComponent: async (): Promise<
      typeof import('./features/onboarding/pages/family-onboarding-page/family-onboarding-page.component').FamilyOnboardingPageComponent
    > =>
      (await import('./features/onboarding/pages/family-onboarding-page/family-onboarding-page.component'))
        .FamilyOnboardingPageComponent,
    data: {
      shellMode: 'app-shell',
      showPrimaryTabbar: false,
    } satisfies AppShellRouteData,
  },
  {
    path: 'timeline',
    canActivate: [authRouteGuard],
    loadComponent: async (): Promise<
      typeof import('./features/mvp-reset/pages/mobile-tab-page/mobile-tab-page.component').MobileTabPageComponent
    > => (await import('./features/mvp-reset/pages/mobile-tab-page/mobile-tab-page.component')).MobileTabPageComponent,
    data: {
      shellMode: 'app-shell',
      mainTabId: 'timeline',
      showPrimaryTabbar: true,
    } satisfies AppShellRouteData,
  },
  {
    path: 'library/items/:id',
    canDeactivate: [libraryItemPendingChangesGuard],
    canActivate: [authRouteGuard],
    loadComponent: async (): Promise<
      typeof import('./features/mvp-reset/pages/library-item-detail-page/library-item-detail-page.component').LibraryItemDetailPageComponent
    > =>
      (await import('./features/mvp-reset/pages/library-item-detail-page/library-item-detail-page.component'))
        .LibraryItemDetailPageComponent,
    data: {
      shellMode: 'app-shell',
      showPrimaryTabbar: false,
    } satisfies AppShellRouteData,
  },
  {
    path: 'library/category/:category',
    canActivate: [authRouteGuard],
    loadComponent: async (): Promise<
      typeof import('./features/mvp-reset/pages/library-category-detail-page/library-category-detail-page.component').LibraryCategoryDetailPageComponent
    > =>
      (await import('./features/mvp-reset/pages/library-category-detail-page/library-category-detail-page.component'))
        .LibraryCategoryDetailPageComponent,
    data: {
      shellMode: 'app-shell',
      mainTabId: 'library',
      showPrimaryTabbar: true,
    } satisfies AppShellRouteData,
  },
  {
    path: 'library',
    canActivate: [authRouteGuard],
    loadComponent: async (): Promise<
      typeof import('./features/mvp-reset/pages/library-page/library-page.component').LibraryPageComponent
    > => (await import('./features/mvp-reset/pages/library-page/library-page.component')).LibraryPageComponent,
    data: {
      shellMode: 'app-shell',
      mainTabId: 'library',
      showPrimaryTabbar: true,
    } satisfies AppShellRouteData,
  },
  {
    path: 'people/new',
    canActivate: [authRouteGuard],
    loadComponent: async (): Promise<
      typeof import('./features/mvp-reset/pages/add-person-page/add-person-page.component').AddPersonPageComponent
    > => (await import('./features/mvp-reset/pages/add-person-page/add-person-page.component')).AddPersonPageComponent,
    data: {
      shellMode: 'app-shell',
      showPrimaryTabbar: false,
    } satisfies AppShellRouteData,
  },
  {
    path: 'people',
    canActivate: [authRouteGuard],
    loadComponent: async (): Promise<
      typeof import('./features/mvp-reset/pages/mobile-tab-page/mobile-tab-page.component').MobileTabPageComponent
    > => (await import('./features/mvp-reset/pages/mobile-tab-page/mobile-tab-page.component')).MobileTabPageComponent,
    data: {
      shellMode: 'app-shell',
      mainTabId: 'people',
      showPrimaryTabbar: true,
    } satisfies AppShellRouteData,
  },
  {
    path: 'people/bindings',
    canActivate: [authRouteGuard],
    loadComponent: async () => (await import('@nexus/customer/sharing-feature')).ProfileBindingsComponent,
    data: { shellMode: 'app-shell', showPrimaryTabbar: false } satisfies AppShellRouteData,
  },
  {
    path: 'people/sharing/advanced',
    canActivate: [authRouteGuard],
    loadComponent: async () => (await import('@nexus/customer/sharing-feature')).SharingPageComponent,
    data: { shellMode: 'app-shell', showPrimaryTabbar: false } satisfies AppShellRouteData,
  },
  {
    path: 'people/sharing',
    canActivate: [authRouteGuard],
    loadComponent: async () => (await import('@nexus/customer/sharing-feature')).FamilyAccessComponent,
    data: {
      shellMode: 'app-shell',
      showPrimaryTabbar: false,
    } satisfies AppShellRouteData,
  },
  {
    path: 'people/shared',
    canActivate: [authRouteGuard],
    loadComponent: async () => (await import('@nexus/customer/sharing-feature')).SharedReadsComponent,
    data: {
      shellMode: 'app-shell',
      showPrimaryTabbar: false,
    } satisfies AppShellRouteData,
  },
  {
    path: 'people/:id',
    canActivate: [authRouteGuard],
    loadComponent: async (): Promise<
      typeof import('./features/mvp-reset/pages/person-profile-page/person-profile-page.component').PersonProfilePageComponent
    > =>
      (await import('./features/mvp-reset/pages/person-profile-page/person-profile-page.component'))
        .PersonProfilePageComponent,
    data: {
      shellMode: 'app-shell',
      showPrimaryTabbar: false,
    } satisfies AppShellRouteData,
  },
  {
    path: 'wishlists/new',
    canActivate: [authRouteGuard],
    loadComponent: async (): Promise<
      typeof import('@nexus/customer/wishlists/feature/create-wishlist').WishlistCreatePageComponent
    > => (await import('@nexus/customer/wishlists/feature/create-wishlist')).WishlistCreatePageComponent,
    data: {
      shellMode: 'app-shell',
      showPrimaryTabbar: false,
    } satisfies AppShellRouteData,
  },
  {
    path: 'wishlists/:wishlistId',
    canActivate: [authRouteGuard],
    loadComponent: async (): Promise<
      typeof import('@nexus/customer/wishlists/feature/wishlist-detail').WishlistDetailPageComponent
    > => (await import('@nexus/customer/wishlists/feature/wishlist-detail')).WishlistDetailPageComponent,
    data: {
      shellMode: 'app-shell',
      showPrimaryTabbar: false,
      wishlistDetailMode: 'authenticated',
    } satisfies WishlistDetailAppRouteData,
  },
  {
    path: 'wishlists',
    canActivate: [authRouteGuard],
    loadComponent: async (): Promise<
      typeof import('@nexus/customer/wishlists/feature/overview').WishlistOverviewPageComponent
    > => (await import('@nexus/customer/wishlists/feature/overview')).WishlistOverviewPageComponent,
    data: {
      shellMode: 'app-shell',
      mainTabId: 'wishlists',
      showPrimaryTabbar: true,
    } satisfies AppShellRouteData,
  },
  {
    path: 'public/wishlists/:shareToken',
    loadComponent: async (): Promise<
      typeof import('@nexus/customer/wishlists/feature/wishlist-detail').WishlistDetailPageComponent
    > => (await import('@nexus/customer/wishlists/feature/wishlist-detail')).WishlistDetailPageComponent,
    data: {
      shellMode: 'bare',
      wishlistDetailMode: 'public',
    } satisfies WishlistDetailAppRouteData,
  },
  {
    path: 'public/wishlist-reservations/confirm',
    loadComponent: async (): Promise<
      typeof import('@nexus/customer/wishlists/feature/confirm-reservation').WishlistConfirmReservationPageComponent
    > =>
      (await import('@nexus/customer/wishlists/feature/confirm-reservation'))
        .WishlistConfirmReservationPageComponent,
    data: {
      shellMode: 'bare',
    } satisfies AppShellRouteData,
  },
  {
    path: 'dev/assistant-probe',
    canActivate: [authRouteGuard],
    loadChildren: async () => {
      const feature = await import('@nexus/customer/assistant/feature-probe');
      return [{ path: '', component: feature.AssistantProbePageComponent, providers: [{
        provide: feature.ASSISTANT_PROBE_TOKEN,
        useFactory: () => { const clerk = inject(ClerkCustomerWebService); return (): Promise<string | null> => clerk.getToken(); },
      }] }];
    },
    data: { shellMode: 'app-shell', showPrimaryTabbar: false } satisfies AppShellRouteData,
  },
  {
    path: 'assistant',
    canActivate: [authRouteGuard],
    loadChildren: async () => {
      const feature = await import('@nexus/customer/assistant/feature-chat');
      return [{ path: '', providers: [feature.AssistantChatState, {
        provide: feature.ASSISTANT_CHAT_TOKEN,
        useFactory: () => { const clerk = inject(ClerkCustomerWebService); return (): Promise<string | null> => clerk.getToken(); },
      }, { provide: feature.ASSISTANT_CHAT_IDENTITY, useFactory: () => inject(ClerkCustomerWebService).subject }], children: [{ path: '', pathMatch: 'full', component: feature.AssistantChatPageComponent },
        { path: 'memory', component: feature.FamilyMemoryPageComponent },
        { path: 'sources/:kind/:id', component: feature.AssistantSourcePageComponent }] }];
    },
    data: { shellMode: 'bare', showPrimaryTabbar: false } satisfies AppShellRouteData,
  },
  {
    path: 'smart-add',
    canActivate: [authRouteGuard],
    canDeactivate: [smartAddPendingChangesGuard],
    loadComponent: async (): Promise<
      typeof import('./features/mvp-reset/pages/smart-add-page/smart-add-page.component').SmartAddPageComponent
    > => (await import('./features/mvp-reset/pages/smart-add-page/smart-add-page.component')).SmartAddPageComponent,
    data: {
      shellMode: 'app-shell',
      showPrimaryTabbar: false,
    } satisfies AppShellRouteData,
  },
  {
    path: 'search',
    canActivate: [authRouteGuard],
    loadComponent: async (): Promise<
      typeof import('./features/mvp-reset/pages/search-page/search-page.component').SearchPageComponent
    > => (await import('./features/mvp-reset/pages/search-page/search-page.component')).SearchPageComponent,
    data: {
      shellMode: 'app-shell',
      showPrimaryTabbar: false,
    } satisfies AppShellRouteData,
  },
  {
    path: 'app/chat',
    pathMatch: 'full',
    redirectTo: 'timeline',
  },
  {
    path: 'app/time',
    pathMatch: 'full',
    redirectTo: 'timeline',
  },
  {
    path: 'app/people',
    pathMatch: 'full',
    redirectTo: 'people',
  },
  {
    path: 'app/library',
    pathMatch: 'full',
    redirectTo: 'library',
  },
  {
    path: 'app/wishlists',
    pathMatch: 'full',
    redirectTo: 'wishlists',
  },
  {
    path: 'thread/new',
    pathMatch: 'full',
    redirectTo: 'timeline',
  },
  {
    path: 'thread/:id',
    pathMatch: 'full',
    redirectTo: 'timeline',
  },
  {
    path: 'invite/:token',
    loadComponent: async (): Promise<
      typeof import('./features/invites/pages/invite-accept-page.component').InviteAcceptPageComponent
    > => (await import('./features/invites/pages/invite-accept-page.component')).InviteAcceptPageComponent,
    data: {
      shellMode: 'bare',
    } satisfies AppShellRouteData,
  },
  {
    path: 'invite',
    loadComponent: async (): Promise<
      typeof import('./features/invites/pages/invite-accept-page.component').InviteAcceptPageComponent
    > => (await import('./features/invites/pages/invite-accept-page.component')).InviteAcceptPageComponent,
    data: {
      shellMode: 'bare',
    } satisfies AppShellRouteData,
  },
  {
    path: 'inbox',
    pathMatch: 'full',
    redirectTo: 'timeline',
  },
  {
    path: 'decision/:id',
    pathMatch: 'full',
    redirectTo: 'timeline',
  },
  {
    path: 'time',
    pathMatch: 'full',
    redirectTo: 'timeline',
  },
  {
    path: 'meetings',
    pathMatch: 'full',
    redirectTo: 'timeline',
  },
  {
    path: 'settings',
    pathMatch: 'full',
    redirectTo: 'timeline',
  },
  {
    path: 'notifications/permission',
    canActivate: [authRouteGuard],
    loadComponent: async (): Promise<
      typeof import('./features/notifications/pages/notifications-permission-page.component').NotificationsPermissionPageComponent
    > =>
      (await import('./features/notifications/pages/notifications-permission-page.component')).NotificationsPermissionPageComponent,
    data: {
      shellMode: 'app-shell',
      showPrimaryTabbar: false,
    } satisfies AppShellRouteData,
  },
];
